/**
  Info:
  # Cookie logic:
  - cookie configurator-options contains all the options available in the theme, i.e. 12v, 18v, etc, these should be set in the theme settings and the landing page should have options to match
  - cookie name is temp-configurator-nameOfOption, i.e. temp-configurator-12v while configuring
  - When configuration gets added to cart, temp cookie is deleted and new cookie (configurator-nameOfOption) is added
  - When customer changes configuration then, no new cookie is set unless customer adds the new configuration to their cart

  - When editing a configuration that was added to the cart, the changes are not stored when customer navigates away from the page
  - If configuration is in cart and cookie is missing, new cookie is created on page load of the set creator
  - If cookie is set but cart is empty, cookie is removed (like when cart/clear is used, otherwise cookie would have been removed when set was removed from cart)

  # Line items properties:
  - Line items get set-creator (value of set, i.e. 12v), set-creator-discount (discount percentage) and _set-has-batteries (boolean) as properties

  # Removing from cart:
  - When configuration gets removed from cart drawer on set page, the cookie is turned into a temp-cookie but the loaded configuration doesn't change. The ATC button label changes from 'Updated Cart' to 'Add to Cart'

  # Inventory handling:
  - before adding to cart, the cart is fetched to see if the item already exists in the cart, if so, we check if it exceeds the max inventory, if so, we show an error
  - There is a custom event 'cart-update' that fires when cart items get updated (in cart drawer, for example), this can potentially be used in the future to expand this logic
  - The modal ATC button can add to the config, even if max inventory is exceeded. Error is shown on actual 'add to cart' event

  # Requirements:
  - Product card 'add to configuration' button needs: variant title, handle, id and price in data attribute 'data-set-configurator-add'. This is done in quickshop.liquid
  - Products with variants can be added from the modal and the main-product.liquid needs an application/json script tag with attribute 'data-set-configurator-object' with title, handle, id and price
  - On add to configuration, all variables are added to the set object
  - On add to cart the set object is added in the cookie

  Full object has these values:
    discount: discount percentage,
    currentTier: current tier,
    products: array of products added,
    totalProducts: total qty of products,
    totalPrice: total price of products,
    set: set option name,
    hasBatteries: boolean, will show/hide text,
    page: the set page url,
    inCart: if this configuration is added to the cart
    cartItems: - only when config is in cart. Is to match the changes to the configuration with items in the cart

  # Theme settings:
    - all set options need to be added in the theme settings under Cart
    - Each section block on the landing page should have one of the values
    - Each set creator page should have the same value in the section settings
 */

class SetCreatorTabs extends HTMLElement {
  constructor() {
    super();

    this.tabs = this.querySelectorAll('input[name="category"]');

    // Add a click event handler to each tab and toggle aria attributes for accessibility
    this.tabs.forEach((tab) => {
      tab.addEventListener('change', (event) => {
        if (event.target.checked) {
          this.toggleTabs(event.target.id);
        }
      });
    });
  }

  toggleTabs(id) {
    const currentActiveEl = this.querySelector('.tabs__button--active');
    currentActiveEl?.classList.remove('tabs__button--active');
    currentActiveEl?.setAttribute('aria-selected', false);

    const currentActiveTab = currentActiveEl.getAttribute('aria-controls');
    document.getElementById(currentActiveTab).setAttribute('aria-hidden', true);

    const clickedLabel = this.querySelector(`[for=${id}]`);
    clickedLabel.classList.add('tabs__button--active');
    clickedLabel.setAttribute('aria-selected', true);

    const newActiveTab = clickedLabel.getAttribute('aria-controls');
    document.getElementById(newActiveTab).setAttribute('aria-hidden', false);
  }
}
if (!customElements.get('set-creator-tabs')) {
  customElements.define('set-creator-tabs', SetCreatorTabs);
}

class SetCreatorPDP extends HTMLElement {
  constructor() {
    super();

    this.options = JSON.parse(this.dataset.options);
    this.addToButton = this.querySelector('[data-set-creator-add-to-config]');
  }

  connectedCallback() {
    this.addToButton.addEventListener('click', this.addToConfig.bind(this));
  }

  addToConfig() {
    // Store in localStorage
    localStorage.setItem('tempProductDetails', JSON.stringify(this.options.product));

    // Redirect to the set configurator page
    window.location.href = this.options.redirectUrl;
  }
}

if (!customElements.get('set-creator-pdp')) {
  customElements.define('set-creator-pdp', SetCreatorPDP);
}

(async () => {
  if (!customElements.get('set-creator')) {
    // get the cart object so we can create an object based on those values if a set exists
    const response = await fetch(routes.cart_url, {
      ...fetchConfig('javascript'),
    });
    const cart = await response.json();

    // get Fancybox
    const { Fancybox } = await import('@fancyapps/ui');

    class SetCreator extends HTMLElement {
      constructor() {
        super();

        // Keep track off all added event listeners so we can remove them when needed
        this.eventListeners = [];

        // Options
        this.options = {};

        // Get options from element data and combine with this.options
        if (this?.dataset?.options) {
          const dataOptions = JSON.parse(this.dataset.options);
          this.options = {
            ...this.options,
            ...dataOptions,
          };
        }

        if (this.options.init_collection === '') return;

        this.configuration = {};
        this.productGrid = this.querySelector('[data-set-creator-products]');
        this.loader = this.productGrid.querySelector('[data-loader]');

        this.selectedItems = this.querySelector('[data-set-creator-items]');
        this.progress = this.querySelector('[data-set-creator-progress]');

        this.totals = this.querySelector('[data-set-creator-totals]');
        this.totalsSubtotal = this.totals.querySelector('[data-set-creator-subtotal-excl-vat]');
        this.totalsVat = this.totals.querySelector('[data-set-creator-vat]');
        this.totalsDiscountWrapper = this.totals.querySelector('[data-set-discount-wrapper]');
        this.totalsDiscountText = this.totals.querySelector('[data-set-discount-text]');
        this.totalsDiscountValue = this.totals.querySelector('[data-set-discount-value]');
        this.totalsTotal = this.totals.querySelector('[data-set-creator-total-incl-vat]');
        this.errorMessageWrapper = this.totals.querySelector('[data-set-creator-cart-error]');
        this.submitBtn = this.totals.querySelector('[data-set-submit]');

        this.batteryText = this.querySelector('[data-set-creator-battery-text]');
        this.placeholderText = this.querySelector('[data-set-creator-placeholder-text]');
        this.setCreatorTabs = document.querySelector('set-creator-tabs');

        this.cart = cart;

        // Check for config cookie
        this.checkCookie();

        // Check if we should show batteries (like when the link was clicked on the cart page or in the drawer):
        const showContent = new URLSearchParams(document.location.search).get('show');
        if (showContent === 'batteries') {
          this.loadBatteryCollection();
        } else {
          // Load the first collection on page load
          this.loadCollection(
            `${window.location.origin}/${this.options.init_collection}`,
            this.options.init_collection_is_battery,
            true,
          );
        }

        // Listen for clicks on collection links so we can load the collection
        this.collectionLinks = this.querySelectorAll('[data-set-configurator-collection]');
        this.collectionLinks.forEach((link) =>
          link.addEventListener('click', (event) => {
            event.preventDefault();
            const isBatteryCategory = 'linkIsBatteryCategory' in event.currentTarget.dataset ? true : false;
            window.tealium.useTealium &&
              tealiumConfiguratorClickCollectionLink(event.currentTarget.dataset.collectionTitle);
            this.loadCollection(event.currentTarget.href, isBatteryCategory);
          }),
        );

        // load battery collection when clicking the link
        this.querySelector('[data-set-load-battery-category]')?.addEventListener(
          'click',
          this.loadBatteryCollection.bind(this),
        );
        // listen for a set to be removed from the cart
        window.addEventListener('set-removed', this.setRemoved.bind(this));

        // listen for 'load more' to be clicked so we can bind new events to the buttons
        window.addEventListener('load-more', () => {
          // Use custom remove all event listener function so we can remove the event listeners when needed
          this.eventListeners = removeAllEventListeners(this.eventListeners);
          this.checkEventListeners();
        });

        // set a cookie for all the available set options. We need this to check the cookies after the checkout
        setCookie('configurator-options', JSON.stringify(this.options.setOptions));

        // form is submitted
        this.submitBtn.addEventListener('click', this.prepareToAdd.bind(this));
      }

      connectedCallback() {
        const tempProductDetails = localStorage.getItem('tempProductDetails');

        if (tempProductDetails) {
          const tempProductDetailsJson = JSON.parse(tempProductDetails);

          if (!tempProductDetailsJson) return;
          this.addPDPItemToConfig(tempProductDetailsJson);

          // Clear the temporary storage
          localStorage.removeItem('tempProductDetails');
        }
      }

      /**
       * Check if the form is valid and if so, add the items to the cart
       * @description This is a separate function so we can use it for the 'add to cart' button as well
       */
      checkEventListeners() {
        // Listen for clicks on ATC form submit
        const addToCartForms = this.productGrid.querySelectorAll('[data-type="add-to-cart-form"]');
        addToCartForms.forEach((form) => {
          form.dataset.isBatteryCategory = this.isBatteryCategory;

          // Use custom add event listener function so we can remove the event listener when needed
          addAndTrackEventListener(this.eventListeners, form, 'submit', this.addItemToConfig.bind(this));
        });

        // Click on product card and add loading item to each product card so we can show it per product
        const productCards = this.productGrid.querySelectorAll('[data-product-card]');
        productCards.forEach((productCard) => {
          // create a new loading animation that will be displayed when customer clicks to get information modal
          const loadingEl = this.loader.cloneNode(true);
          loadingEl.classList.add('hidden');
          productCard.parentNode.append(loadingEl);

          // add event listener for the image and the text part of the product card
          productCard
            .querySelectorAll('[data-set-creator-load-product]')
            .forEach((clickObj) => clickObj?.addEventListener('click', this.fetchProductInfo.bind(this)));

          // the 'more info' button for variants should also load the modal
          const atcButton = productCard.querySelector('[data-product-card-add-to-cart]');
          if (atcButton && 'productCardButtonMore' in atcButton.dataset) {
            // Use custom add event listener function so we can remove the event listener when needed
            addAndTrackEventListener(this.eventListeners, atcButton, 'click', (event) => {
              event.preventDefault();
              this.fetchProductInfo(event);
            });
          }
        });
      }

      /**
       *
       * @param {CustomEvent} event
       * Triggered when item is removed from the cart.
       * Cookie is changed to be temp-cookie again while on the configurator page
       */
      setRemoved(event) {
        // only continue if we're currently editing the set that was removed
        if (event.detail.set !== this.options.set) return;

        // store the current config products in case the configuration was changed before the set was removed
        const products = this.configuration.products;

        this.configuration = getCookie(`configurator-${event.detail.set}`);
        this.configuration.inCart = false;
        this.configuration.products = products;

        // update the button text
        this.submitBtn.querySelector('[data-set-submit-add]').classList.toggle('hidden');
        this.submitBtn.querySelector('[data-set-submit-update]').classList.toggle('hidden');

        // set the temp cookie
        setCookie(`temp-configurator-${event.detail.set}`, JSON.stringify(this.configuration));

        // remove the cart cookie
        removeCookie(`configurator-${event.detail.set}`);
      }

      /**
       *
       * @param {String} collectionUrl
       * @returns collection products
       */
      async loadCollection(collectionUrl, isBatteryCategory, isInit = false) {
        if (!collectionUrl) return;

        try {
          // Show loader
          this.toggleLoadingAnim('show');

          // Get the products
          const response = await fetch(`${collectionUrl}?section_id=collection-set-creator-content`);
          if (!response.ok) throw new Error(response.status);

          const products = await response.text();
          const parsedHTML = new DOMParser().parseFromString(products, 'text/html');

          // add the products to the grid
          this.productGrid.querySelector('#CollectionProductGrid').innerHTML =
            parsedHTML.getElementById('products').innerHTML;

          // scroll collection into view, only when page has already been loaded
          if (!isInit) {
            window.scrollTo({
              top: this.productGrid.offsetTop - 40,
              behavior: 'smooth',
            });
            setTimeout(() => {
              const topOffset = window.innerWidth >= 1024 ? 140 : 84;
              if (document.body.classList.contains('header--visible')) {
                window.scrollTo({
                  top: this.productGrid.offsetTop - topOffset - 40,
                  behavior: 'smooth',
                });
              }
            }, 300);
          }

          this.isBatteryCategory = isBatteryCategory;
          this.checkEventListeners();
        } catch (error) {
          debug() && console.error('Error getting products:', error);
        } finally {
          // update the rating for the product cards
          if (window.apps.reviewsIo) getRating();

          // Hide loader
          this.toggleLoadingAnim('hide');
        }
      }

      loadBatteryCollection() {
        // check the collapsible classes
        const currentActiveCollapsible = this.setCreatorTabs.querySelector(
          '[data-collapsible-group].collapsible-is-open',
        );
        currentActiveCollapsible.classList.remove('collapsible-is-open');

        const firstBatteryCollapsibleCategory = this.setCreatorTabs.querySelectorAll(
          '[data-collapsible-group-is-battery="true"]',
        )[0];
        firstBatteryCollapsibleCategory.classList.add('collapsible-is-open');

        // get the first battery category id and check the input
        const firstBatteryCategory = this.querySelectorAll('[data-tab-is-battery="true"]')[0];
        const batteryId = firstBatteryCategory.id;
        firstBatteryCategory.checked = true;

        // load the battery category
        const firstBatteryCollectionURL = this.querySelectorAll('[data-link-is-battery-category]')[0].href;
        this.loadCollection(firstBatteryCollectionURL, true);

        // toggle the tabs
        this.setCreatorTabs.toggleTabs(batteryId);
      }

      /**
       * On page load, we need to check if a configuration exists, if so, we load the content that was already configured, else we start a clean configuration
       */
      checkCookie() {
        // check cookies to see if a selection has already been made
        const tempCookieValue = getCookie(`temp-configurator-${this.options.set}`);
        const cookieCartValue = getCookie(`configurator-${this.options.set}`);

        // no cookies
        if (this.cart && !tempCookieValue && !cookieCartValue) {
          // get only the set items
          const cart_items = this.cart.items.filter((item) => item.properties['set-creator'] === this.options.set);

          // redirect when the cart is empty or there are no set items
          if (this.cart.item_count === 0 || cart_items.length === 0) {
            // in the theme editor, this will give issues, so check
            if (debug() === false) {
              window.location.href = this.options.landing_page;
              return false;
            }
          }

          // get all items for the current set page
          this.configuration.products = [];
          for (let index = 0; index < cart_items.length; index++) {
            const product = cart_items[index];

            // when a item has more than 1 qty, add them individually to the config object
            if (product.quantity > 1) {
              let q = 0;
              while (q < product.quantity) {
                this.configuration.products.push({
                  title: product.title,
                  id: product.id,
                  sku: product.sku,
                  vendor: product.vendor,
                  qty: 1,
                  handle: product.handle,
                  price: product.price,
                  weeeFee: product.properties['WEEE_FEE_EX'] || '',
                });
                q++;
              }
            } else {
              this.configuration.products.push({
                title: product.title,
                id: product.id,
                qty: product.quantity,
                handle: product.handle,
                sku: product.sku,
                vendor: product.vendor,
                price: product.price,
                weeeFee: product.properties['WEEE_FEE_EX'] || '',
              });
            }
          }

          if (debug() === false) {
            this.configuration.discount = cart_items[0].properties['set-creator-discount'];
            this.configuration.hasBatteries = cart_items[0].properties['_set-has-batteries'];
            this.configuration.totalProducts = cart_items.reduce((total, product) => total + product.quantity, 0);
            this.configuration.totalPrice = cart_items.reduce((total, product) => total + product.price, 0);
            this.configuration.set = this.options.set;
            this.configuration.page = window.location.pathname;
            this.configuration.inCart = true;
          }

          // Get the current tier
          this.getCurrentTier();

          // load content from cookie
          this.loadConfiguration();

          // set the cookie
          setTimeout(() => {
            setCookie(`configurator-${this.options.set}`, JSON.stringify(this.configuration));
          }, 250);
        }
        // if a cookie exists, first check if there is a cart cookie and load the values, else check if a temp cookie exists
        else if ((this.cart && tempCookieValue) || cookieCartValue) {
          // first check if we have a cart configuration, which is leading, else check if there is a configuration that hasn't been changed
          if (cookieCartValue) {
            this.configuration = cookieCartValue;
            this.configuration.page = window.location.pathname;

            // if there are no items, change the cart cookie to a temp cookie
            if (this.cart.item_count === 0) {
              this.configuration.inCart = false;

              // set the temp cookie
              setCookie(`temp-configurator-${this.options.set}`, JSON.stringify(this.configuration));

              // remove the cart cookie
              removeCookie(`configurator-${this.options.set}`);
            }

            // load content from cookie
            this.loadConfiguration();
          } else if (tempCookieValue) {
            this.configuration = tempCookieValue;
            this.configuration.page = window.location.pathname;

            // check if there is a configuration
            if (!this.configuration.products || this.configuration.products.length === 0) {
              this.configuration.products = [];
              this.configuration.currentTier = 0;
              this.configuration.previousTier = 0;
              this.configuration.inCart = false;
            }

            // load content from cookie
            this.loadConfiguration();
          }
        }
      }

      loadConfiguration() {
        // if this configuration is already in the cart, store the current cart items
        if (this.configuration.inCart) {
          this.configuration.cartItems = [...this.configuration.products];
        }

        // update the button
        this.submitBtn.querySelector('[data-set-submit-add]').classList.toggle('hidden', this.configuration.inCart);
        this.submitBtn.querySelector('[data-set-submit-update]').classList.toggle('hidden', !this.configuration.inCart);

        // show/hide the battery text
        this.batteryText?.classList.toggle('hidden', this.configuration.hasBatteries);

        // hide the placeholder text
        this.placeholderText.classList.toggle('hidden', this.configuration.products.length > 0);

        // update the previous tier to the current tier on page load
        this.configuration.previousTier = this.configuration.currentTier;

        // update products
        this.updateItems();

        // update tier
        this.updateTierProgress();

        // update totals
        this.updateTotals();

        // show content
        this.classList.remove('loading');
      }

      /**
       *
       * @param {Event} event
       * @param {Object} Fancybox
       * Event when a product is added to the configuration
       */
      addItemToConfig(event, Fancybox) {
        event.preventDefault();

        this.selectedItems.querySelector('[data-loader]').classList.remove('hidden');

        // Parse JSON from the button data attribute containing the variant ID and add the selected quantity to the object as well
        const submitButton = event.target.querySelector('[data-set-configurator-add]') || event.target;

        const variantValuesObj =
          submitButton.dataset.setConfiguratorAdd === undefined
            ? JSON.parse(event.target.closest('product-info').querySelector('[type="application/json"]').textContent)
            : JSON.parse(submitButton.dataset.setConfiguratorAdd);

        const isBattery = event.target.dataset.isBatteryCategory === 'true';

        // Add the selected quantity to the variant object
        variantValuesObj.maxQty = variantValuesObj.productStock;
        let qtyToAdd = 1;

        // check if we're not adding more than allowed
        if (qtyToAdd > variantValuesObj.maxQty) {
          qtyToAdd = variantValuesObj.maxQty;
        }

        // get the total current items
        const totalOfThisVariant = this.configuration.products.reduce((total, product) => {
          product.id === variantValuesObj.variantId ? (total += product.qty) : total;
          return total;
        }, 0);

        // Item is added even though max has been reached
        if (totalOfThisVariant >= variantValuesObj.maxQty || variantValuesObj.maxQty === 0) {
          // show 'max reached' notice
          let p = document.createElement('p');
          p.classList.add(...['set-creator__stock-notice', 'is-col-12']);
          p.textContent = this.options.max_reached_label;
          const target =
            event.target.closest('.product-card__quickshop-wrapper') || event.target.closest('product-info');
          target.after(p);
          submitButton.setAttribute('disabled', true);

          // remove the p again
          setTimeout(() => {
            p.remove();
            submitButton.removeAttribute('disabled');
          }, 3000);

          // make items clickable again
          this.selectedItems.querySelector('[data-loader]').classList.add('hidden');

          return;
        } else if (Fancybox) {
          Fancybox.close();
        }

        // Load the product(s)
        let q = 0;
        while (q < qtyToAdd) {
          this.updateItems(variantValuesObj.productHandle, variantValuesObj.productTitle);

          // update the object with the new product
          this.configuration.products = [
            ...this.configuration.products,
            {
              id: variantValuesObj.variantId,
              title: variantValuesObj.productTitle,
              qty: 1,
              sku: variantValuesObj.productSku,
              vendor: variantValuesObj.productVendor,
              handle: variantValuesObj.productHandle,
              price: variantValuesObj.variantPrice,
              maxQty: variantValuesObj.maxQty,
              isBattery: isBattery,
              weeeFee: variantValuesObj.weeeFee,
            },
          ];
          q++;
        }

        // Trigger Tealium event when product has been added to the configurator
        window.tealium.useTealium && tealiumAddProductToConfigurator(variantValuesObj.productTitle);

        // update the config object
        setTimeout(() => {
          this.updateConfiguration();
        }, 100);
      }

      addPDPItemToConfig(productDetails) {
        const variantValuesObj = productDetails;

        // Check the current total of this variant in the configuration
        this.configuration.products.reduce((total, product) => {
          return product.id === variantValuesObj.variantId ? total + product.qty : total;
        }, 0);

        const isBattery = variantValuesObj.isBattery || false;

        // Add the product(s) to the configuration
        this.updateItems(variantValuesObj.productHandle, variantValuesObj.productTitle);

        this.configuration.products.push({
          id: variantValuesObj.variantId,
          title: variantValuesObj.productTitle,
          qty: 1,
          sku: variantValuesObj.productSku,
          vendor: variantValuesObj.productVendor,
          handle: variantValuesObj.productHandle,
          price: variantValuesObj.variantPrice,
          isBattery: isBattery,
          weeeFee: variantValuesObj.weeeFee,
        });

        // update the config object
        setTimeout(() => this.updateConfiguration(), 100);
      }

      /**
       *
       * @param {String} handle
       * @param {Object} card
       */
      removeItem(handle, card) {
        // show the loader
        this.selectedItems.querySelector('[data-loader]').classList.remove('hidden');

        // remove the element
        card.remove();

        // remove the item from the array
        const handleIndex = this.configuration.products.findIndex((product) => product.handle === handle);
        this.configuration.products.splice(handleIndex, 1);

        // update the config object
        setTimeout(() => {
          this.updateConfiguration();
        }, 100);
      }

      getCurrentTier() {
        // Get the current discount tier
        for (let i = 0; i < this.options.tiers.length; i++) {
          const tier = this.options.tiers[i];

          // the first tier
          if (this.configuration.totalProducts < tier.product) {
            if (!this.options.tiers[i - 1]) {
              this.configuration.discount = 0;
              this.configuration.currentTier = 0;
            } else {
              this.configuration.discount = this.options.tiers[i - 1].discount;
              this.configuration.currentTier = i;
            }
            break;
          }
          // last tier achieved
          else if (this.configuration.totalProducts >= tier.product) {
            this.configuration.discount = tier.discount;
            this.configuration.currentTier = i + 1;
          }
        }
      }

      updateConfiguration() {
        // get the total products and price
        this.configuration.totalProducts = this.configuration.products.length;
        this.configuration.totalPrice = this.configuration.products.reduce(
          (total, product) => total + parseInt(product.price),
          0,
        );
        this.configuration.hasBatteries = this.configuration.products.some((product) => product.isBattery);

        // show/hide placeholder text
        this.configuration.totalProducts > 0
          ? this.placeholderText.classList.add('hidden')
          : this.placeholderText.classList.remove('hidden');

        // Get the current tier;
        this.getCurrentTier();

        // update the cookie object
        const cookieObject = {
          discount: this.configuration.discount,
          currentTier: this.configuration.currentTier,
          products: this.configuration.products,
          totalProducts: this.configuration.totalProducts,
          totalPrice: this.configuration.totalPrice,
          set: this.options.set,
          hasBatteries: this.configuration.hasBatteries,
          page: this.configuration.page,
          inCart: this.configuration.inCart,
        };

        // set the temp cookie
        setCookie(`temp-configurator-${this.options.set}`, JSON.stringify(cookieObject));

        // show/hide battery text
        this.batteryText?.classList.toggle('hidden', this.configuration.hasBatteries);

        // make items clickable again and remove error message
        this.selectedItems.querySelector('[data-loader]').classList.add('hidden');
        this.errorMessageWrapper.classList.add('hidden');

        // update tier
        this.updateTierProgress();

        // update totals
        this.updateTotals();
      }

      updateTierProgress() {
        // the text element to update
        const tierTextWrapper = this.querySelector('[data-tier-text]');
        const tierTextCompleteWrapper = this.querySelector('[data-tier-text-complete]');

        // no tier reached yet, update text only
        if (this.configuration.currentTier === 0) {
          tierTextWrapper.querySelector('[data-tier-products-text]').textContent =
            this.options.tiers[this.configuration.currentTier].product - this.configuration.totalProducts ||
            this.options.tiers[0].product;
          tierTextWrapper.querySelector('[data-tier-discount-text]').textContent =
            this.options.tiers[this.configuration.currentTier].discount;

          this.querySelector(`[data-tier="${this.configuration.currentTier}"`).classList.remove(
            'discount-progress__item--checked',
          );
          return;
        }

        // all tiers reached
        const updateText = this.options.tiers.length === this.configuration.currentTier ? false : true;
        tierTextCompleteWrapper.classList.toggle(
          'hidden',
          this.options.tiers.length !== this.configuration.currentTier,
        );
        tierTextWrapper.classList.toggle('hidden', this.options.tiers.length === this.configuration.currentTier);

        // check if there is a difference in tiers
        let direction;
        if (this.configuration.previousTier !== this.configuration.currentTier) {
          this.configuration.previousTier > this.configuration.currentTier
            ? (direction = 'remove')
            : (direction = 'add');
        }
        // tier hasn't changed, update text
        else {
          // set variables so we can find all active tiers
          let n = 0;
          while (n < this.configuration.currentTier) {
            this.querySelector(`[data-tier="${n}"`).classList.add('discount-progress__item--checked');
            if (updateText) {
              tierTextWrapper.querySelector('[data-tier-products-text]').textContent =
                this.options.tiers[this.configuration.currentTier].product - this.configuration.totalProducts;
              tierTextWrapper.querySelector('[data-tier-discount-text]').textContent =
                this.options.tiers[this.configuration.currentTier].discount;
            }
            n++;
          }
          return;
        }

        // remove the class
        if (direction === 'remove') {
          this.querySelector(`[data-tier="${this.configuration.currentTier}"`).classList.remove(
            'discount-progress__item--checked',
          );
        }

        // check to see which tier to add
        if (direction === 'add') {
          let n = 0;
          while (n < this.configuration.currentTier) {
            this.querySelector(`[data-tier="${n}"`).classList.add('discount-progress__item--checked');
            n++;
          }
        }

        if (updateText) {
          tierTextWrapper.querySelector('[data-tier-products-text]').textContent =
            this.options.tiers[this.configuration.currentTier].product - this.configuration.totalProducts;
          tierTextWrapper.querySelector('[data-tier-discount-text]').textContent =
            this.options.tiers[this.configuration.currentTier].discount;
        }

        // update the object
        this.configuration.previousTier = this.configuration.currentTier;
      }

      updateTotals() {
        // show/hide the wrapper and enable/disable the submit button
        if (!this.configuration?.totalPrice || this.configuration.totalPrice === 0) {
          this.totals.classList.add('hidden');
          this.submitBtn.setAttribute('disabled', true);
          return;
        } else {
          this.totals.classList.remove('hidden');
          this.submitBtn.removeAttribute('disabled');
        }

        const vat = 1 + parseInt(this.options.vat) * 0.01;
        const discountTotal = this.configuration.totalPrice * (this.configuration.discount * 0.01);
        const total = this.configuration.totalPrice - discountTotal;
        const vatTotal = Math.round(total - total / vat);
        const subtotal = this.configuration.totalPrice - vatTotal;

        this.totalsSubtotal.innerHTML = `${Shopify.formatMoney(subtotal)}`;
        this.totalsVat.innerHTML = `${Shopify.formatMoney(vatTotal)}`;
        this.totalsTotal.innerHTML = `${Shopify.formatMoney(this.configuration.totalPrice - discountTotal)}`;

        // update the discount texts
        if (this.configuration.discount !== 0) {
          this.totalsDiscountWrapper.classList.remove('hidden');
          this.totalsDiscountValue.innerHTML = `${Shopify.formatMoney(discountTotal * -1)}`;
          this.totalsDiscountText.innerHTML = `${this.configuration.discount}% ${this.options.discount_label}`;
        } else {
          this.totalsDiscountWrapper.classList.add('hidden');
        }

        // scroll totals into view
        window.scrollTo({
          top: this.querySelector('[data-set-creator-form]').offsetTop,
          behavior: 'smooth',
        });
        setTimeout(() => {
          const topOffset = window.innerWidth >= 1024 ? 140 : 84;
          if (document.body.classList.contains('header--visible')) {
            window.scrollTo({
              top: this.querySelector('[data-set-creator-form]').offsetTop - topOffset,
              behavior: 'smooth',
            });
          }
        }, 500);
      }

      /**
       * Update the set overview
       * @param {String} handle
       * @param {String} title
       */
      updateItems(handle, title) {
        // when adding a new product, we pass the handle and the title
        if (handle && title) {
          this.fetchProduct(handle, title);
        } else {
          // on load, get all the products
          this.configuration.products.forEach((product) => this.fetchProduct(product.handle, product.title));
        }
      }

      /**
       * Get the product to display in the configuration overview
       * @param {String} handle
       * @param {String} title
       */
      async fetchProduct(handle, title) {
        // get content from product card to update the right column without having to do a fetch
        await fetch(`/products/${handle}?view=product-card-small`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

            // return if fetch returns 404 page
            if (parsedHTML.body.classList.contains('template-404')) return;

            // get the Product Card
            const productCardHTML = parsedHTML.getElementById('product-card')?.querySelector('.product-card');

            if (productCardHTML) {
              const titleString = productCardHTML.querySelector('.product-card__title').innerHTML || title;

              productCardHTML.querySelector('.product-card__title').innerHTML = titleString;

              // create a new element for 'remove' button
              const newRemoveButton = document.createElement('button');
              newRemoveButton.classList.add(...['product-card__remove', 'button', 'button--icon']);
              newRemoveButton.dataset.productHandle = handle;
              newRemoveButton.ariaLabel = this.options.remove_label.replace('[[ item ]]', titleString);
              newRemoveButton.innerHTML = `
              <svg class="icon icon-close icon-sm" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" focusable="false" viewBox="0 0 12 12">
                <path d="M6.70713 6L11.3536 10.6464L10.6464 11.3536L6.00001 6.70713L1.35363 11.3538L0.646381 10.6465L5.29288 6L0.646255 1.35363L1.35351 0.646378L6.00001 5.29288L10.6464 0.646378L11.3536 1.35363L6.70713 6Z" fill="currentColor"/>
              </svg>`;

              productCardHTML.dataset.productCard = handle;
              productCardHTML.append(newRemoveButton);

              // Add eventlistener for the remove button
              newRemoveButton.addEventListener('click', this.removeItem.bind(this, handle, productCardHTML));

              // appending the slide will automatically trigger the class and init the product card swiper
              this.selectedItems.append(productCardHTML);
            }
          })
          .catch((error) => {
            debug() && console.error(error);
          })
          .finally(() => {});
      }

      /**
       * Check if cart items should be removed first or if items can even be added based on qty
       * @param {Event} event
       */
      async prepareToAdd(event) {
        event.preventDefault();

        const submitButton = event.target;
        submitButton.setAttribute('disabled', true);
        submitButton.classList.add('loading');

        const updates = {};
        const reducedCartItems = {};

        // Get the cart again, in case something changed
        await this.getCart();

        // if there are cart items already for this configuration, find the line-item key, also get the cart item id and the quantity so we can match it to the new configuration to see if it doesn't exceed max inventory quantities
        for (let i = 0; i < this.cart.items.length; i++) {
          const cartItem = this.cart.items[i];
          const currCount = reducedCartItems[cartItem.id] ?? 0;
          if (cartItem.properties['set-creator'] === this.options.set) {
            updates[cartItem.key] = 0;
          } else {
            reducedCartItems[cartItem.id] = currCount + cartItem.quantity;
          }
        }

        // check the inventory
        const checkInventory = this.checkInventory(reducedCartItems);
        if (!checkInventory.inventoryAllowed) {
          this.errorMessageWrapper.textContent = checkInventory.errorMessage;
          this.errorMessageWrapper.classList.remove('hidden');
          this.errorMessageWrapper.focus();

          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');

          // we can't add the item, so return (error message is displayed in the this.checkInventory function)
          return;
        }

        // if a set is already in the cart, check if the new configuration is different, if so remove the old configuration and add the new one, else get the cart object and create a new object to add
        if (this.configuration.cartItems) {
          // create a test function to match the current config array and the cart items array
          const isSame = (array1, array2) => {
            if (array1.length !== array2.length) return false;
            array1.sort();
            array2.sort();

            return array1.toString() === array2.toString();
          };

          // if the configuration is the same as the cart items, just go to the cart
          if (isSame(this.configuration.cartItems, this.configuration.products)) {
            window.location.href = routes.cart_url;
            return;
          }

          // Remove all items from the cart that are part of this set
          const body = JSON.stringify({ updates });
          try {
            const response = await fetch(`${routes.cart_update_url}.js`, {
              ...fetchConfig('javascript'),
              ...{ body },
            });
            if (!response.ok) throw new Error(response.status);

            // On success: items were removed, add the new products
            // const parsedState = await response.json();

            this.addNewProducts(submitButton);
          } catch (error) {
            debug() && console.error('Error removing set', error);
          }
        } else {
          this.addNewProducts(submitButton);
        }
      }

      /**
       * Add all added items to the cart
       * @param {Element} submitButton
       */
      async addNewProducts(submitButton) {
        // create a new array with the configured products and their totals; will return an object with key/value of variant_id/total qty
        const reducedProducts = this.configuration.products.reduce((allProducts, product) => {
          const currCount = (allProducts[product.id] && parseInt(allProducts[product.id].count)) ?? 0;
          return {
            ...allProducts,
            [product.id]: {
              count: currCount + product.qty,
              sku: product.sku,
              vendor: product.vendor,
              title: product.title,
              price: product.price,
              weeeFee: product.weeeFee,
            },
          };
        }, {});

        const items = [];

        for (const key in reducedProducts) {
          if (Object.hasOwnProperty.call(reducedProducts, key)) {
            const { sku, vendor, title, price, count: quantity } = reducedProducts[key];
            items.push({
              id: parseInt(key),
              quantity,
              sku,
              vendor,
              title,
              price,
              properties: {
                'set-creator': this.options.set,
                'set-creator-discount': this.configuration.discount,
                '_set-has-batteries': this.configuration.hasBatteries,
                WEEE_FEE_EX: reducedProducts[key].weeeFee === '' ? undefined : reducedProducts[key].weeeFee,
              },
            });
          }
        }

        // add the items to the cart
        const body = JSON.stringify({ items });

        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            ...fetchConfig(),
            ...{ body },
          });
          if (!response.ok) throw new Error(response.status);

          // On success, set cookie and go to cart page
          // const parsedState = await response.json();

          // set the cookie by getting the current cookie, adding a inCart value
          const cookieObject = getCookie(`temp-configurator-${this.options.set}`);
          cookieObject.inCart = true;

          setCookie(`configurator-${this.options.set}`, JSON.stringify(cookieObject));

          // fire Tealium event
          if (window.tealium.useTealium) {
            tealiumAddProducts({
              product_id: items.map((item) => item.sku),
              product_name: items.map((item) => item.title),
              product_price: items.map((item) => (item.price / 100.0).toFixed(2)),
              product_brand: items.map((item) => item.vendor),
              product_quantity: items.map((item) => item.quantity),
            });
          }

          // redirect to the cart
          setTimeout(() => {
            window.location.href = routes.cart_url;
          }, 250);
        } catch (error) {
          debug() && console.error('Error adding to cart', error);

          // only remove loading when there's an error
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');
        }
      }

      /**
       *
       * @param {Object} itemsObject
       * @returns true if the inventory to be added is allowed.
       * Each product card has a data-attribute set in the product-card.liquid where the total is calculated based on cart items.
       */
      checkInventory(itemsObject) {
        let inventoryAllowed = true,
          quantity,
          title;

        // create a reduced object with the total for each configured product, so we can match it with the cart items.
        const reducedConfiguredProducts = this.configuration.products.reduce((allProducts, product) => {
          const currCount = allProducts[product.id]?.qty ?? 0;
          return {
            ...allProducts,
            [product.id]: {
              qty: currCount + product.qty,
              maxQty: product.maxQty,
              title: product.title,
            },
          };
        }, {});

        for (const product in reducedConfiguredProducts) {
          if (Object.hasOwnProperty.call(reducedConfiguredProducts, product)) {
            if (!inventoryAllowed) break;

            const productObj = reducedConfiguredProducts[product];
            const configQty = productObj.qty;
            const maxQty = productObj.maxQty;
            title = productObj.title;

            // if there are more items than allowed in configuration, return false
            if (productObj.qty > productObj.maxQty) {
              inventoryAllowed = false;
              quantity = maxQty;
            }

            // if the product id is found in the cart items, check if the qty exceeds max quantity, if so, return with an error
            else if (Object.keys(itemsObject).length > 0 && product in itemsObject) {
              const cartQty = itemsObject[product];
              if (configQty + cartQty > maxQty) {
                inventoryAllowed = false;
                quantity = maxQty - cartQty;
              }
            }
          }
        }

        // return an object with the error message of item cannot be added
        return {
          inventoryAllowed,
          errorMessage: inventoryAllowed
            ? ''
            : quantity === 0
              ? this.options.inventory_error_text_0.replace('[[ count ]]', quantity).replace('[[ product ]]', title)
              : this.options.inventory_error_text.replace('[[ count ]]', quantity).replace('[[ product ]]', title),
        };
      }

      /**
       * Get the product info to display in a modal
       * @param {Event} event 'more info' button or product card click
       */
      async fetchProductInfo(event) {
        // check if Fancybox exists
        if (typeof Fancybox === 'undefined') return;

        const productCard = event.target.closest('[data-product-card]');
        const url = productCard.dataset.productCard;

        if (!url) return;

        try {
          // show loader
          this.toggleLoadingAnim('show', productCard.parentNode, '.product-card');

          // get content from product page
          const response = await fetch(`${window.location.origin}${url}`);
          const responseText = await response.text();

          if (!response.ok) throw new Error(response.status);

          // on success:
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

          // remove the skip links
          parsedHTML.querySelector('[href="#ProductContent"]').remove();
          parsedHTML.querySelector('[href="#ProductInfo"]').remove();

          // clone the 'add to configuration' button so it will add the product to the config instead of adding it to cart
          const productCardButton = productCard.querySelector('[data-product-card-add-to-cart]');
          const newProductCardButton = productCardButton?.cloneNode(true);
          if (!newProductCardButton) return;

          // create the fancybox, when done, update the qty input and button
          Fancybox.show([
            {
              src: parsedHTML.querySelector('.product-wrapper'),
              type: 'html',
              preload: false,
            },
          ]);
        } catch (error) {
          debug() && console.error(error);
        } finally {
          // Hide loader
          this.toggleLoadingAnim('hide', productCard.parentNode, '.product-card');
        }
      }

      /**
       *
       * @param {String} type show/hide
       * @param {Element} element when added, the function checks for the loading spinner in the element and adds opacity to the selector
       */
      toggleLoadingAnim(type, element, selector) {
        const loadingEl = element ? element.querySelector('[data-loader]') : this.loader;
        const wrapperEL = element
          ? element.querySelector(selector)
          : this.productGrid.querySelector('#CollectionProductGrid');

        if (!loadingEl || !wrapperEL) return;

        type === 'show' ? (wrapperEL.style.opacity = 0.4) : (wrapperEL.style.opacity = 1);
        loadingEl.classList.toggle('hidden', type !== 'show');
      }

      async getCart() {
        try {
          const response = await fetch(routes.cart_url, {
            ...fetchConfig('javascript'),
          });
          if (!response.ok) throw new Error(response.status);
          this.cart = await response.json();
        } catch (error) {
          debug() && console.error(error);
        }
      }
    }
    customElements.define('set-creator', SetCreator);
    window.SetCreator = SetCreator;
  }
})();
